import { type ReactNode, useState } from "react";
import { coerceThemePreference, type ThemePreference } from "@jackedlogs/shared";
import { ThemeProvider } from "@jackedlogs/ui";

// Host wrapper for the shared ThemeContext (@jackedlogs/ui). Owns the web-rnw
// side of theme persistence: a localStorage cache so the picked
// palette/mode survives reloads even before the Profile fetch lands (mirrors
// apps/mobile/src/theme/MobileThemeProvider.tsx's AsyncStorage cache, and
// apps/web's now-legacy theme.ts localStorage key). Profile persistence
// (cross-device) is handled separately by ThemeProfileBridge — this
// component only ever touches the local cache key, never /api/profiles.
//
// Unlike mobile's AsyncStorage read (async, lands post-mount), localStorage
// is synchronous — the cache is read once at component-init time so the
// first paint already reflects the user's saved preference (no flash of the
// default Catppuccin theme).
const STORAGE_KEY = "jl.themePreference.v1";

function readCache(): ThemePreference | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    return coerceThemePreference(JSON.parse(raw));
  } catch {
    return null;
  }
}

function persist(pref: ThemePreference): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
  } catch {
    // Best-effort — fall back to DEFAULT_THEME_PREFERENCE next load.
  }
}

export function WebThemeProvider({ children }: { children: ReactNode }) {
  const [initialPreference] = useState<ThemePreference | null>(() => readCache());

  return (
    <ThemeProvider initialPreference={initialPreference} onChange={persist}>
      {children}
    </ThemeProvider>
  );
}
